import { useEffect, useState } from "react";
import { teacherApi, type IncomingRequest, type PrepSummary } from "@/services/api/teacher";
import { formatDay, useRadarStatusLabel } from "./talkRadarReadFormat";
import { TalkPrepBand } from "./talkPrepBand";
import { useTalkT } from "./useTalkT";

type ListRow = {
  id: string;
  date: string | null;
  subject_name: string | null;
  klass_name: string | null;
  room_name: string | null;
  status: string;
  total_lessons: number;
  lessons_filled: number;
  lessons_pending: number;
  lessons_declined: number;
  prep_summary?: PrepSummary;
};

function fromIncoming(card: IncomingRequest): ListRow {
  return {
    id: card.id,
    date: card.date,
    subject_name: card.subject_name,
    klass_name: card.klass_name,
    room_name: card.room_name,
    status: card.status,
    total_lessons: card.lesson_count,
    lessons_filled: card.lessons_accepted ?? 0,
    lessons_pending: card.lessons_pending ?? 0,
    lessons_declined: card.lessons_declined ?? 0,
    prep_summary: card.prep_summary,
  };
}

function useTalkRadarList(
  requestIds: string[],
  role: "admin" | "teacher",
  kind: "incoming" | "own"
) {
  const [rows, setRows] = useState<ListRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setError(null);
    setRows([]);
    Promise.all(
      requestIds.map(async (id) => {
        try {
          return await teacherApi.getRequestDetails(id);
        } catch {
          if (role === "teacher" && kind === "incoming") {
            return fromIncoming(await teacherApi.getIncomingRequestDetail(id));
          }
          throw new Error("details");
        }
      })
    )
      .then((next) => {
        if (live) setRows(next);
      })
      .catch(() => {
        if (live) setError("Anfragen konnten nicht geladen werden");
      });
    return () => {
      live = false;
    };
  }, [requestIds, role, kind]);

  return { rows, error };
}

function metaLine(row: ListRow): string {
  return row.klass_name || "";
}

export function TalkRadarList({
  requestIds,
  onOpen,
  role,
  kind = "incoming",
}: {
  requestIds: string[];
  onOpen: (
    id: string,
    meta?: { status?: string | null; date?: string | null }
  ) => void;
  role: "admin" | "teacher";
  kind?: "incoming" | "own";
}) {
  const t = useTalkT();
  const statusLabel = useRadarStatusLabel();
  const { rows, error } = useTalkRadarList(requestIds, role, kind);
  return (
    <div className="talk-radar-pick">
      <p className="talk-radar-pick-title">
        {t("talk_radar_pick", { count: requestIds.length })}
      </p>
      {error && <p>{error}</p>}
      <ul className="talk-radar-list">
        {rows.map((row) => {
          const meta = metaLine(row);
          return (
            <li key={row.id}>
                <button
                  type="button"
                  className="talk-radar-card"
                  onClick={() => onOpen(row.id, { status: row.status, date: row.date })}
                >
                <span className="talk-radar-card-top">
                  <span>{formatDay(row.date)}</span>
                  <span className={`talk-radar-status is-${row.status}`}>
                    {statusLabel(row.status)}
                  </span>
                </span>
                <span className="talk-radar-card-title">{row.subject_name || "—"}</span>
                {role === "teacher" && <TalkPrepBand summary={row.prep_summary} />}
                {meta && <span className="talk-radar-card-meta">{meta}</span>}
                <span className="talk-radar-card-counts">
                  <span>
                    {row.total_lessons}{" "}
                    {row.total_lessons === 1 ? "Lektion" : "Lektionen"}
                  </span>
                  <span className="is-filled">{row.lessons_filled} akzeptiert</span>
                  {row.lessons_pending > 0 && (
                    <span className="is-pending">{row.lessons_pending} offen</span>
                  )}
                  {row.lessons_declined > 0 && (
                    <span className="is-declined">{row.lessons_declined} abgelehnt</span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
